import {
  typedCollection,
  typedDoc,
  getDocument,
  getCollection,
  onSnapshot,
  orderBy,
  where,
} from '@/lib/firebase/firestore'
import { getOrderReceiptFn, type ShopOrderReceiptResponse } from '@/lib/firebase/functions'
import type {
  ShopProductDocument,
  ShopOrderDocument,
  ShopSyncMetaDocument,
} from '../types'

// ── Collections ───────────────────────────────────────────────────────────────

const shopProductsCollection = typedCollection<ShopProductDocument>('shopProducts')
const shopOrdersCollection = typedCollection<ShopOrderDocument>('shopOrders')

// ── Products ──────────────────────────────────────────────────────────────────

export async function getAllProducts(): Promise<ShopProductDocument[]> {
  return getCollection(shopProductsCollection, orderBy('title'))
}

export async function getProductById(id: string): Promise<ShopProductDocument | null> {
  const ref = typedDoc<ShopProductDocument>('shopProducts', id)
  const data = await getDocument(ref)
  if (!data) return null
  return { ...data, id }
}

// ── Orders ────────────────────────────────────────────────────────────────────

export async function getShopOrderReceipt(
  orderId: string,
  receiptToken: string
): Promise<ShopOrderReceiptResponse> {
  const response = await getOrderReceiptFn({ kind: 'shop', orderId, receiptToken })
  if (response.data.kind !== 'shop') {
    throw new Error('Unexpected receipt type.')
  }
  return response.data
}

export async function getOrdersByUserId(
  userId: string
): Promise<Array<ShopOrderDocument & { id: string }>> {
  return getCollection(
    shopOrdersCollection,
    where('userId', '==', userId),
    orderBy('createdAt', 'desc')
  )
}

// ── Sync metadata ─────────────────────────────────────────────────────────────

export function subscribeToSyncMeta(
  callback: (meta: ShopSyncMetaDocument | null) => void
): () => void {
  const ref = typedDoc<ShopSyncMetaDocument>('shopMeta', 'sync')
  return onSnapshot(ref, (snap) => {
    callback(snap.exists() ? snap.data() : null)
  })
}
